import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { incrementActionCounter } from "../../utils/actionCounters";
import { fetchPolicies, fetchPoliciesForMultipleUsers } from "../policies/policySlice";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import PaidIcon from '@mui/icons-material/Paid';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Pagination from "@mui/material/Pagination";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FiSearch } from "react-icons/fi";
import { useAdminStaffData } from "../../hooks/useAdminStaffData";
import { useAccountStatus } from "../../hooks/useAccountStatus";
import RemindDialog from "../../components/common/RemindDialog";
import dayjs from "dayjs";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Badge from '@mui/material/Badge';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getAuth } from 'firebase/auth';
import { firebaseCache } from "../../utils/firebaseCache";
import { COMPANIES, POLICY_TYPES } from "../../data/dropdowns";
import IconButton from '@mui/material/IconButton';

// Helper to convert values like "25,000", "₹25000", null -> number
const toNumber = (val) => {
    if (val == null) return 0;
    const cleaned = String(val).replace(/[^0-9.-]/g, "");
    const num = Number(cleaned);
    return isNaN(num) ? 0 : num;
};

const FinancialDonut = ({ received, balance, size = 110 }) => {
    const total = received + balance;

    // Safety check
    if (total === 0) return (
        <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle cx={size / 2} cy={size / 2} r={(size / 2) - 10} fill="transparent" stroke="#F3F4F6" strokeWidth={Math.max(6, size * 0.1)} />
            </svg>
            <Typography sx={{ position: 'absolute', fontSize: 10, color: '#9CA3AF' }}>No Data</Typography>
        </Box>
    );

    const strokeWidth = Math.max(6, size * 0.1);
    const radius = (size / 2) - (strokeWidth / 2) - 2;
    const center = size / 2;
    const circumference = 2 * Math.PI * radius;

    // Simplified Logic: 2 segments only
    // Green = Amount Paid (Received)
    // Red = Balance Pending

    const paidRatio = received / total;
    const balanceRatio = balance / total;

    // Offsets
    const paidOffset = circumference - (paidRatio * circumference);
    const balanceOffset = circumference - (balanceRatio * circumference);
    const balanceRotation = -90 + (paidRatio * 360);

    return (
        <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.05))' }}>
                {/* Background */}
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="transparent"
                    stroke="#F3F4F6"
                    strokeWidth={strokeWidth}
                />

                {/* Paid Segment (Green) */}
                {received > 0 && (
                    <circle
                        cx={center}
                        cy={center}
                        r={radius}
                        fill="transparent"
                        stroke="#22C55E"
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={paidOffset}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${center} ${center})`}
                        style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
                    />
                )}

                {/* Balance Segment (Red) */}
                {balance > 0 && (
                    <circle
                        cx={center}
                        cy={center}
                        r={radius}
                        fill="transparent"
                        stroke="#EF4444"
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={balanceOffset}
                        strokeLinecap="round"
                        transform={`rotate(${balanceRotation} ${center} ${center})`}
                        style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
                    />
                )}
            </svg>
            <Box sx={{ position: 'absolute', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography sx={{ fontSize: size * 0.12, fontWeight: 800, color: '#1F2937', lineHeight: 1, mb: 0.1 }}>{Math.round(balanceRatio * 100)}%</Typography>
                <Typography sx={{ fontSize: size * 0.075, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '-0.04em', lineHeight: 1, transform: 'scale(0.9)', transformOrigin: 'center' }}>Pending</Typography>
            </Box>
        </Box>
    );
};

const AdminFinance = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector((state) => state.auth);
    const { policies, loading } = useSelector((state) => state.policies);
    const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();
    const { isAccountActive } = useAccountStatus();

    // State
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState("motor");
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [remindDialogOpen, setRemindDialogOpen] = useState(false);
    const [selectedPolicyForRemind, setSelectedPolicyForRemind] = useState(null);

    // Responsive breakpoints
    const isTinyScreen = useMediaQuery('(max-height:720px)');
    const isShortScreen = useMediaQuery('(max-height:900px)');
    const isMediumScreen = useMediaQuery('(max-height:1080px)');
    const itemsPerPage = isTinyScreen ? 4 : isShortScreen ? 5 : isMediumScreen ? 6 : 7;
    const categories = ["motor", "non-motor", "life"];
    const isSmallScreen = useMediaQuery('(max-width:1400px)');
    const isNarrowScreen = useMediaQuery('(max-width:1200px)');

    // Filter state
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedPolicyTypes, setSelectedPolicyTypes] = useState({});
    const [showCompanyOptions, setShowCompanyOptions] = useState(false);
    const [showPolicyTypeOptions, setShowPolicyTypeOptions] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [showDateRangeCalendar, setShowDateRangeCalendar] = useState(null);
    const calendarRef = useRef();
    const isInitialMount = useRef(true);

    // Upload dialog state
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [uploadCompany, setUploadCompany] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    // Fetch policies
    useEffect(() => {
        if (staffLoading) return;

        if (isAdmin && userIds.length > 0) {
            console.log('Admin Finance - fetching aggregated policies:', userIds);
            dispatch(fetchPoliciesForMultipleUsers(userIds));
        } else if (user?.uid && !isAdmin) {
            dispatch(fetchPolicies(user.uid));
        }
    }, [user?.uid, dispatch, isAdmin, userIds, staffLoading]);

    // Restore filter state from sessionStorage
    useEffect(() => {
        const savedFilters = sessionStorage.getItem('adminFinance_filterState');
        if (savedFilters) {
            try {
                const filters = JSON.parse(savedFilters);
                setSelectedCompanies(filters.selectedCompanies || []);
                setSelectedPolicyTypes(filters.selectedPolicyTypes || {});
                setShowCompanyOptions(filters.showCompanyOptions || false);
                setShowPolicyTypeOptions(filters.showPolicyTypeOptions || false);
                if (filters.dateRange) {
                    setDateRange({
                        start: filters.dateRange.start ? dayjs(filters.dateRange.start) : null,
                        end: filters.dateRange.end ? dayjs(filters.dateRange.end) : null
                    });
                }
            } catch (error) {
                console.error('Error restoring filter state:', error);
            }
        }
        setTimeout(() => { isInitialMount.current = false; }, 100);
    }, []);

    // Save filter state to sessionStorage
    useEffect(() => {
        if (isInitialMount.current) return;
        const filterState = {
            selectedCompanies,
            selectedPolicyTypes,
            showCompanyOptions,
            showPolicyTypeOptions,
            dateRange: {
                start: dateRange.start ? dateRange.start.toISOString() : null,
                end: dateRange.end ? dateRange.end.toISOString() : null
            }
        };
        sessionStorage.setItem('adminFinance_filterState', JSON.stringify(filterState));
    }, [selectedCompanies, selectedPolicyTypes, showCompanyOptions, showPolicyTypeOptions, dateRange]);

    // Handle navigation state
    useEffect(() => {
        if (location.state?.currentPage) setCurrentPage(location.state.currentPage);
        if (location.state?.fromCategory) setSelectedCategory(location.state.fromCategory);
    }, [location.state]);

    // Filter handlers
    const handleFilterButtonClick = (event) => setFilterAnchorEl(event.currentTarget);
    const handleFilterClose = () => setFilterAnchorEl(null);
    const handleCompanyToggle = (company) => {
        setSelectedCompanies((prev) =>
            prev.includes(company) ? prev.filter((c) => c !== company) : [...prev, company]
        );
    };
    const handlePolicyTypeToggle = (policyType) => {
        setSelectedPolicyTypes((prev) => {
            const currentTabTypes = prev[selectedCategory] || [];
            const updated = currentTabTypes.includes(policyType)
                ? currentTabTypes.filter((t) => t !== policyType)
                : [...currentTabTypes, policyType];
            return { ...prev, [selectedCategory]: updated };
        });
    };
    const handleCompanyCheckboxChange = (checked) => {
        setShowCompanyOptions(checked);
        if (!checked) setSelectedCompanies([]);
    };
    const handlePolicyTypeCheckboxChange = (checked) => {
        setShowPolicyTypeOptions(checked);
        if (!checked) setSelectedPolicyTypes((prev) => ({ ...prev, [selectedCategory]: [] }));
    };
    const clearFilters = () => {
        setSelectedCompanies([]);
        setSelectedPolicyTypes({});
        setShowCompanyOptions(false);
        setShowPolicyTypeOptions(false);
        setDateRange({ start: null, end: null });
    };
    const handleDateRangeCalendarChange = (date) => {
        const selectedDay = dayjs(date);
        if (showDateRangeCalendar === 'start') {
            setDateRange((prev) => ({
                start: selectedDay,
                end: prev.end && selectedDay.isAfter(prev.end) ? null : prev.end
            }));
            setShowDateRangeCalendar('end');
        } else if (showDateRangeCalendar === 'end') {
            if (selectedDay.isBefore(dateRange.start, 'day')) return;
            setDateRange((prev) => ({ start: prev.start, end: selectedDay }));
            setShowDateRangeCalendar(null);
        }
    };

    const activeFilterCount = selectedCompanies.length + ((selectedPolicyTypes[selectedCategory] || []).length) + (dateRange.start && dateRange.end ? 1 : 0);

    // Filter policies
    const filtered = useMemo(() => {
        return policies.filter((p) => {
            if (p.status === "New Lead") return false;

            const categoryMatch =
                selectedCategory === "motor" ? p.policyCategory === "motor"
                    : selectedCategory === "non-motor" ? p.policyCategory === "non-motor"
                        : p.policyCategoryType === selectedCategory;
            if (!categoryMatch) return false;

            // Company filter
            if (selectedCompanies.length > 0 && !selectedCompanies.includes(p.policyCompany)) return false;

            // Policy type filter
            const currentTabPolicyTypes = selectedPolicyTypes[selectedCategory] || [];
            if (currentTabPolicyTypes.length > 0 && !currentTabPolicyTypes.includes(p.policyType)) return false;

            // Date range filter
            if (dateRange.start && dateRange.end) {
                const created = p.createdAt ? (p.createdAt.seconds ? dayjs(p.createdAt.seconds * 1000) : dayjs(p.createdAt)) : null;
                if (!created || !created.isValid()) return false;
                const start = dateRange.start.startOf('day');
                const end = dateRange.end.endOf('day');
                if (created.isBefore(start) || created.isAfter(end)) return false;
            }

            const searchLower = searchTerm.toLowerCase();
            const hitsBase = (p.name || "").toLowerCase().includes(searchLower) || (p.mobile || "").includes(searchTerm);

            if (selectedCategory === "motor") {
                const searchClean = searchLower.replace(/-/g, "");
                const vehicleNumberClean = (p.vehicleNumber || "").toLowerCase().replace(/-/g, "");
                return hitsBase || vehicleNumberClean.includes(searchClean);
            }
            return hitsBase;
        });
    }, [policies, selectedCategory, selectedCompanies, selectedPolicyTypes, dateRange, searchTerm]);

    // Sort by date (newest first)
    const sorted = useMemo(() => {
        return [...filtered].sort((a, b) => {
            const dateA = a.createdAt ? (a.createdAt.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt)) : new Date(0);
            const dateB = b.createdAt ? (b.createdAt.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt)) : new Date(0);
            return dateB - dateA;
        });
    }, [filtered]);

    const paginated = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const totalPages = Math.ceil(sorted.length / itemsPerPage);

    // Calculate summary values
    const summaryValues = useMemo(() => {
        let totalPremium = 0;
        let pendingBalance = 0;
        let totalCommission = 0;
        let totalDiscount = 0;

        filtered.forEach((p) => {
            const netPremium = toNumber(p.netPremium);
            const premium = toNumber(p.premiumAmount || p.totalPremium);
            const amountPayable = toNumber(p.amountPayable) || (premium - toNumber(p.discount));
            const paid = toNumber(p.paidAmount ?? p.amountPaid ?? p.amountReceived);
            const balance = Math.max(0, amountPayable - paid);
            const commission = toNumber(p.totalCommission);
            const discount = toNumber(p.discount) || (premium - amountPayable);

            totalPremium += netPremium;
            pendingBalance += balance;
            totalCommission += commission;
            totalDiscount += discount;
        });

        const netProfit = totalCommission - totalDiscount - pendingBalance;

        return { totalPremium, pendingBalance, totalCommission, netProfit };
    }, [filtered]);

    // Calculate counts with cross-tab filtering
    const categoryCounts = useMemo(() => {
        const counts = { motor: 0, 'non-motor': 0, life: 0 };
        policies.forEach((p) => {
            if (p.status === "New Lead") return;
            if (selectedCompanies.length > 0 && !selectedCompanies.includes(p.policyCompany)) return;
            if (dateRange.start && dateRange.end) {
                const created = p.createdAt ? (p.createdAt.seconds ? dayjs(p.createdAt.seconds * 1000) : dayjs(p.createdAt)) : null;
                if (!created || !created.isValid()) return;
                const start = dateRange.start.startOf('day');
                const end = dateRange.end.endOf('day');
                if (created.isBefore(start) || created.isAfter(end)) return;
            }
            if (p.policyCategory === 'motor') counts.motor++;
            else if (p.policyCategory === 'non-motor') counts['non-motor']++;
            else if (p.policyCategoryType === 'life') counts.life++;
        });
        return counts;
    }, [policies, selectedCompanies, dateRange]);

    // Format amount helper
    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
    };

    // Handle navigation to detail
    const handleRowClick = (row) => {
        navigate(`/commission/${row.id}`, { state: { from: '/balance', currentPage, fromCategory: selectedCategory, policy: row } });
    };

    // Handle row expansion
    const toggleRowExpansion = (e, rowId) => {
        e.stopPropagation();
        const isOpening = expandedRowId !== rowId;
        setExpandedRowId(isOpening ? rowId : null);

        if (isOpening) {
            // Increased delay and used 'center' block for guaranteed visibility
            setTimeout(() => {
                const element = document.getElementById(`expanded-row-container-${rowId}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 200);
        }
    };

    // Generate WhatsApp message for balance reminders
    const generateBalanceMessage = async (policy) => {
        const premium = toNumber(policy.premiumAmount || policy.totalPremium);
        const amountPayable = toNumber(policy.amountPayable) || (premium - toNumber(policy.discount));
        const paid = toNumber(policy.paidAmount ?? policy.amountPaid ?? policy.amountReceived);
        const balanceAmount = amountPayable - paid;

        if (balanceAmount <= 0) return "";

        const policyType = policy.policyType || "Policy";
        const companyName = policy.policyCompany || "your insurer";
        const isMotor = policy.policyCategory === "motor";
        const vehicleMake = policy.vehicleMake || "";
        const vehicleModel = policy.vehicleModel || "";
        const vehicleNumber = policy.vehicleNumber || "";
        const clientName = policy.name || "";
        const greeting = clientName ? `Hi ${clientName}` : "Hi";

        let message = "";
        let agentName = 'your agent';
        let agentMobile = '';

        try {
            if (user?.uid) {
                const agentDoc = await getDoc(doc(db, 'agent-details', user.uid));
                if (agentDoc.exists()) {
                    const agentData = agentDoc.data();
                    agentName = agentData.name || agentData.displayName || 'your agent';
                    agentMobile = agentData.phone || agentData.phoneNumber || '';
                }
            }
        } catch (error) {
            console.error('Error fetching agent details:', error);
        }

        if (isMotor && vehicleModel && vehicleNumber) {
            message = `${greeting}\nYour pending balance of *₹${formatAmount(balanceAmount)}* for your *${policyType} policy* ,${vehicleMake} ${vehicleModel} - *${vehicleNumber}* with ${companyName} is still due❗\n\nPlease pay at the earliest, for more information contact your agent: ${agentName} at ${agentMobile}`;
        } else if (isMotor && vehicleNumber) {
            message = `${greeting}\nYour pending balance of *₹${formatAmount(balanceAmount)}* for your *${policyType} policy* ,${vehicleMake} - *${vehicleNumber}* with ${companyName} is still due❗\n\nPlease pay at the earliest, for more information contact your agent: ${agentName} at ${agentMobile}`;
        } else {
            message = `${greeting}\nYour pending balance of *₹${formatAmount(balanceAmount)}* for your *${policyType}* with ${companyName} is still due❗\n\nPlease pay at the earliest, for more information contact your agent: ${agentName} at ${agentMobile}`;
        }

        return message;
    };

    // Handle WhatsApp redirect
    const handleWhatsAppRedirect = (message) => {
        if (!message) return;
        const whatsappAppUrl = `whatsapp://send?text=${encodeURIComponent(message)}`;
        const webWhatsappUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(message)}`;

        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = whatsappAppUrl;
        document.body.appendChild(iframe);
        setTimeout(() => { document.body.removeChild(iframe); }, 100);

        let appOpened = false;
        const handleBlur = () => { appOpened = true; window.removeEventListener('blur', handleBlur); };
        window.addEventListener('blur', handleBlur);

        setTimeout(() => {
            window.removeEventListener('blur', handleBlur);
            if (!appOpened) {
                window.open(webWhatsappUrl, "_blank", "noopener,noreferrer");
            }
        }, 1500);
    };

    const handleRemindClick = (policy, event) => {
        event.stopPropagation();
        setSelectedPolicyForRemind(policy);
        setRemindDialogOpen(true);
    };

    const handleRemindConfirm = async () => {
        if (selectedPolicyForRemind) {
            try {
                const message = await generateBalanceMessage(selectedPolicyForRemind);
                handleWhatsAppRedirect(message);
                if (user?.uid) {
                    await incrementActionCounter(user.uid, 'balanceremindercounter');
                }
            } catch (error) {
                console.error('Error generating balance message:', error);
            }
        }
        setRemindDialogOpen(false);
        setSelectedPolicyForRemind(null);
    };

    const handleRemindClose = () => {
        setRemindDialogOpen(false);
        setSelectedPolicyForRemind(null);
    };

    // Upload handlers
    const handleUploadClick = () => {
        setUploadDialogOpen(true);
        setUploadFile(null);
        setUploadResult(null);
        setUploadError(null);
        setUploadCompany('');
    };

    const handleUploadClose = () => {
        if (uploadResult?.success) {
            console.log("🔄 Commission upload successful, refreshing data...");
            firebaseCache.markForInvalidation('policy');
            if (isAdmin && userIds.length > 0) {
                dispatch(fetchPoliciesForMultipleUsers({ userIds, forceRefresh: true }));
            } else if (user?.uid && !isAdmin) {
                dispatch(fetchPolicies({ agentId: user.uid, forceRefresh: true }));
            }
        }
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadResult(null);
        setUploadError(null);
        setUploadCompany('');
    };

    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        processSelectedFile(file);
    };

    const processSelectedFile = (file) => {
        if (file) {
            const ext = file.name.split('.').pop().toLowerCase();
            const allowedPdf = ['pdf'];
            const allowedExcel = ['xlsx', 'xls'];

            if (uploadCompany === 'GODIGIT') {
                if (allowedExcel.includes(ext)) {
                    setUploadFile(file);
                    setUploadError(null);
                } else {
                    setUploadError('Please select an Excel file (.xlsx or .xls)');
                }
            } else {
                if (allowedPdf.includes(ext)) {
                    setUploadFile(file);
                    setUploadError(null);
                } else {
                    setUploadError('Please select a PDF file');
                }
            }
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processSelectedFile(files[0]);
        }
    };

    const handleUploadSubmit = async () => {
        if (!uploadFile) return;

        setUploadLoading(true);
        setUploadError(null);
        setUploadResult(null);

        try {
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();

            if (!token) throw new Error('Not authenticated');
            if (!uploadCompany) throw new Error('Please select a company');

            const formData = new FormData();
            formData.append('pdf', uploadFile);
            formData.append('company', uploadCompany);

            const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5001';
            const response = await fetch(`${backendUrl}/api/commission/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.message || result.error || 'Upload failed');

            setUploadResult(result);

            if (result.successfullyUpdated > 0) {
                firebaseCache.markForInvalidation('policy');
                // Increment commission upload counter
                if (auth.currentUser?.uid) {
                    incrementActionCounter(auth.currentUser.uid, 'commissionuploadcounter');
                }
                if (isAdmin && userIds.length > 0) {
                    dispatch(fetchPoliciesForMultipleUsers({ userIds, forceRefresh: true }));
                } else if (user?.uid) {
                    dispatch(fetchPolicies({ agentId: user.uid, forceRefresh: true }));
                }
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploadError(error.message || 'Failed to upload commission PDF');
        } finally {
            setUploadLoading(false);
        }
    };

    // Calculate policy-specific values
    const getPolicyValues = (policy) => {
        const premium = toNumber(policy.premiumAmount || policy.totalPremium);
        const discount = toNumber(policy.discount);
        const amountPayable = toNumber(policy.amountPayable) || (premium - discount);
        const amountReceived = toNumber(policy.paidAmount ?? policy.amountPaid ?? policy.amountReceived);
        const balance = Math.max(0, amountPayable - amountReceived);
        const netPremium = toNumber(policy.netPremium);
        const commission = toNumber(policy.totalCommission);

        return { premium, discount, amountPayable, amountReceived, balance, netPremium, commission };
    };

    return (
        <Box sx={{ px: 2, pt: 1 }}>
            {/* Header with Summary Cards */}
            <Box sx={{
                display: 'flex',
                flexDirection: isNarrowScreen ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isNarrowScreen ? 'flex-start' : 'flex-start',
                mb: isNarrowScreen ? 1 : 2,
                gap: isNarrowScreen ? 1 : 0
            }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontFamily: 'Roboto Slab',
                        fontWeight: 550,
                        fontSize: isNarrowScreen ? '20px' : isSmallScreen ? '24px' : '32px',
                        lineHeight: isNarrowScreen ? '32px' : isSmallScreen ? '40px' : '56px',
                        letterSpacing: '0.02em',
                        color: '#212121',
                        mb: isNarrowScreen ? 1 : 0
                    }}
                >
                    Finance
                </Typography>

                <Box sx={{
                    display: 'flex',
                    gap: isNarrowScreen ? 1 : 2,
                    flexWrap: 'wrap',
                    width: isNarrowScreen ? '100%' : 'auto'
                }}>
                    {/* Total Net Premium */}
                    <Paper elevation={0} sx={{
                        p: isNarrowScreen ? 1 : 1.5,
                        minWidth: isNarrowScreen ? 100 : 160,
                        flex: isNarrowScreen ? '1 1 auto' : 1,
                        border: '1px solid #E0E0E0',
                        borderRadius: 2
                    }}>
                        <Typography sx={{ fontSize: isNarrowScreen ? 10 : 12, color: '#6B7280', mb: 0.5 }}>Total Net Premium</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: '#374151' }}>
                            <CurrencyRupeeIcon sx={{ fontSize: isNarrowScreen ? 14 : 16 }} />
                            <Typography sx={{ fontWeight: 600, fontSize: isNarrowScreen ? 14 : 16 }}>
                                {formatAmount(summaryValues.totalPremium)}
                            </Typography>
                        </Box>
                    </Paper>

                    {/* Pending Balance */}
                    <Paper elevation={0} sx={{
                        p: isNarrowScreen ? 1 : 1.5,
                        minWidth: isNarrowScreen ? 100 : 160,
                        flex: isNarrowScreen ? '1 1 auto' : 1,
                        border: '1px solid #E0E0E0',
                        borderRadius: 2
                    }}>
                        <Typography sx={{ fontSize: isNarrowScreen ? 10 : 12, color: '#6B7280', mb: 0.5 }}>Pending Balance</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: '#DC2626' }}>
                            <CurrencyRupeeIcon sx={{ fontSize: isNarrowScreen ? 14 : 16 }} />
                            <Typography sx={{ fontWeight: 600, fontSize: isNarrowScreen ? 14 : 16 }}>
                                {formatAmount(summaryValues.pendingBalance)}
                            </Typography>
                        </Box>
                    </Paper>

                    {/* Total Commission */}
                    <Paper elevation={0} sx={{
                        p: isNarrowScreen ? 1 : 1.5,
                        minWidth: isNarrowScreen ? 100 : 160,
                        flex: isNarrowScreen ? '1 1 auto' : 1,
                        border: '1px solid #E0E0E0',
                        borderRadius: 2
                    }}>
                        <Typography sx={{ fontSize: isNarrowScreen ? 10 : 12, color: '#6B7280', mb: 0.5 }}>Total Commission</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: '#166534' }}>
                            <CurrencyRupeeIcon sx={{ fontSize: isNarrowScreen ? 14 : 16 }} />
                            <Typography sx={{ fontWeight: 600, fontSize: isNarrowScreen ? 14 : 16 }}>
                                {formatAmount(summaryValues.totalCommission)}
                            </Typography>
                        </Box>
                    </Paper>

                    {/* Net Profit */}
                    <Paper elevation={0} sx={{
                        p: isNarrowScreen ? 1 : 1.5,
                        minWidth: isNarrowScreen ? 100 : 160,
                        flex: isNarrowScreen ? '1 1 auto' : 1,
                        border: '1px solid #E0E0E0',
                        borderRadius: 2
                    }}>
                        <Typography sx={{ fontSize: isNarrowScreen ? 10 : 12, color: '#6B7280', mb: 0.5 }}>Net Profit</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: '#0063F7' }}>
                            <CurrencyRupeeIcon sx={{ fontSize: isNarrowScreen ? 14 : 16 }} />
                            <Typography sx={{ fontWeight: 600, fontSize: isNarrowScreen ? 14 : 16 }}>
                                {formatAmount(summaryValues.netProfit)}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    background: '#fff',
                    borderRadius: '0',
                    boxShadow: 'none',
                    px: { xs: 2, sm: 3 },
                    pb: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    gap: 0,
                    minHeight: 'calc(100vh - 160px)',
                }}
            >
                {/* Tabs and Filters */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tabs
                        value={selectedCategory}
                        onChange={(e, newVal) => {
                            setSelectedCategory(newVal);
                            setCurrentPage(1);
                            setExpandedRowId(null);
                        }}
                        variant="standard"
                        size="small"
                        indicatorColor="primary"
                        sx={{
                            mb: 1,
                            width: 'fit-content',
                            '& .MuiTab-root': {
                                textTransform: 'capitalize',
                                fontWeight: 500,
                                minWidth: isSmallScreen ? 60 : 80,
                                px: isNarrowScreen ? 1 : isSmallScreen ? 1.5 : 2,
                                py: 0.5,
                                fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '13px' : 'inherit',
                                borderRadius: 1,
                                '&:hover': { backgroundColor: '#E0EBFF' },
                            }
                        }}
                    >
                        {categories.map((cat) => (
                            <Tab
                                key={cat}
                                label={`${cat.charAt(0).toUpperCase() + cat.slice(1)} (${categoryCounts[cat] || 0})`}
                                value={cat}
                                sx={{ '&:hover': { backgroundColor: '#E0EBFF' } }}
                            />
                        ))}
                    </Tabs>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {isAdmin && (
                            <Button
                                variant="outlined"
                                startIcon={<UploadFileIcon />}
                                onClick={handleUploadClick}
                                disabled={!isAccountActive}
                                sx={{
                                    borderRadius: '8px',
                                    px: isSmallScreen ? 1.5 : 2.5,
                                    height: isSmallScreen ? 32 : 40,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: isSmallScreen ? '13px' : 'inherit',
                                    color: '#0063F7',
                                    borderColor: '#E0E0E0',
                                    '&:hover': {
                                        backgroundColor: '#F5F8FF',
                                        borderColor: '#0063F7',
                                        color: '#004DC2'
                                    }
                                }}
                            >
                                Upload Commission
                            </Button>
                        )}
                        <Badge color="primary" badgeContent={activeFilterCount} invisible={activeFilterCount === 0}>
                            <Button
                                variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
                                startIcon={<FilterAltOutlinedIcon />}
                                onClick={handleFilterButtonClick}
                                sx={{
                                    borderRadius: '8px',
                                    px: isSmallScreen ? 1.5 : 2.5,
                                    height: isSmallScreen ? 32 : 40,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: isSmallScreen ? '13px' : 'inherit',
                                    backgroundColor: activeFilterCount > 0 ? '#0063F7' : '#fff',
                                    color: activeFilterCount > 0 ? '#fff' : '#1F2937',
                                    borderColor: '#E0E0E0',
                                    '&:hover': {
                                        color: activeFilterCount > 0 ? '#fff' : '#004DC2',
                                        backgroundColor: activeFilterCount > 0 ? '#0056d6' : '#F5F8FF'
                                    }
                                }}
                            >
                                Filter
                            </Button>
                        </Badge>
                    </Box>
                </Box>
                <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 2, mt: -1 }} />

                {/* Search */}
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search client by name, phone number, or vehicle number"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <Box sx={{ px: 1, display: 'flex', alignItems: 'center' }}>
                                <FiSearch />
                            </Box>
                        )
                    }}
                    sx={{ mb: 0.5 }}
                />

                {loading ? (
                    <Typography>Loading policies...</Typography>
                ) : (
                    <>
                        <TableContainer
                            component={Paper}
                            sx={{
                                boxShadow: 'none',
                                border: 'none',
                                backgroundColor: 'transparent',
                                borderRadius: 0,
                                px: 0,
                                mx: { xs: -2, sm: -3 },
                                width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' },
                                overflow: 'visible',
                                '& .MuiTableHead-root .MuiTableCell-root': {
                                    borderBottom: '1px solid #E0E0E0',
                                    backgroundColor: 'transparent',
                                },
                                '& .MuiTableBody-root .MuiTableCell-root': {
                                    borderBottom: '1px solid #E0E0E0',
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            <Table
                                size={isNarrowScreen ? "small" : "medium"}
                                aria-label="admin finance table"
                                sx={{
                                    border: 'none',
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                    tableLayout: 'fixed',
                                    '& th, & td': { border: 'none', px: isNarrowScreen ? 0.3 : 0 },
                                    '& td:first-of-type, & th:first-of-type': { pl: 0 },
                                    '& td:nth-of-type(2), & th:nth-of-type(2)': { pl: isNarrowScreen ? 1 : 2 },
                                    '& tr': { borderBottom: '1px solid #E0E0E0' },
                                }}
                            >
                                <TableHead>
                                    <TableRow sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                        <TableCell sx={{ width: 48, px: 0, textAlign: 'center' }} />
                                        <TableCell sx={{ width: isNarrowScreen ? '20%' : '18%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {!isNarrowScreen && <PersonIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />}
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Customer</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: isNarrowScreen ? '14%' : '14%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {selectedCategory === "motor" ? (
                                                    <>
                                                        {!isNarrowScreen && <DirectionsCarIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />}
                                                        <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Vehicle</Typography>
                                                    </>
                                                ) : (
                                                    <>
                                                        {!isNarrowScreen && <DescriptionIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />}
                                                        <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Category</Typography>
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: isNarrowScreen ? '14%' : '14%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {!isNarrowScreen && <DescriptionIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />}
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Policy Type</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: isNarrowScreen ? '11%' : '14%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {!isNarrowScreen && <BusinessIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />}
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Company</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: isNarrowScreen ? '14%' : '12%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: isNarrowScreen ? 'flex-start' : 'flex-start' }}>
                                                {!isNarrowScreen && <PaidIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />}
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Net Prem</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: isNarrowScreen ? '14%' : '12%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: isNarrowScreen ? 'flex-start' : 'flex-start' }}>
                                                {!isNarrowScreen && <CurrencyRupeeIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />}
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Comm</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: isNarrowScreen ? '13%' : '12%', px: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: isNarrowScreen ? 'flex-end' : 'flex-start', pr: isNarrowScreen ? 3 : 0 }}>
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#4B5563DE', whiteSpace: 'nowrap' }}>Status</Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginated.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                                    <Typography sx={{
                                                        fontFamily: 'Roboto',
                                                        fontWeight: 400,
                                                        fontSize: '16px',
                                                        lineHeight: '24px',
                                                        letterSpacing: '0.02em',
                                                        color: '#6B7280',
                                                        mb: 1
                                                    }}>
                                                        No policies found. Great time to follow up with new leads!
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginated.map((row, index) => {
                                            const values = getPolicyValues(row);
                                            const isExpanded = expandedRowId === row.id;

                                            return (
                                                <React.Fragment key={row.id || index}>
                                                    <TableRow
                                                        key={row.id || index}
                                                        onClick={() => handleRowClick(row)}
                                                        sx={{
                                                            height: 48,
                                                            verticalAlign: 'middle',
                                                            cursor: 'pointer',
                                                            backgroundColor: isExpanded ? '#F5F8FF' : 'transparent',
                                                            // Standard row hover for data cells (Customer to Status)
                                                            '&:hover td:not(:first-of-type)': {
                                                                backgroundColor: '#E0EBFF'
                                                            },
                                                            // When arrow cell is hovered, highlight only it and clear data cell hover
                                                            '& td:first-of-type:hover': {
                                                                backgroundColor: '#E0EBFF !important'
                                                            },
                                                            '& td:first-of-type:hover ~ td': {
                                                                backgroundColor: 'transparent !important'
                                                            }
                                                        }}
                                                    >

                                                        <TableCell
                                                            sx={{
                                                                px: 0,
                                                                width: 48,
                                                                minWidth: 48,
                                                                verticalAlign: 'middle',
                                                                pl: 0, // Removed extra left padding
                                                                pr: 0, // Removed extra right padding
                                                                textAlign: 'center',
                                                                cursor: 'pointer',
                                                                transition: 'background-color 0.15s ease',
                                                            }}
                                                            onClick={(e) => toggleRowExpansion(e, row.id)}
                                                        >
                                                            {isExpanded ? (
                                                                <KeyboardArrowUpIcon sx={{ fontSize: 22, color: '#0063F7' }} />
                                                            ) : (
                                                                <KeyboardArrowDownIcon sx={{ fontSize: 22, color: '#0063F7' }} />
                                                            )}
                                                        </TableCell>

                                                        <TableCell sx={{ pl: isNarrowScreen ? 1 : 2, pr: 0, width: isNarrowScreen ? '20%' : '18%', verticalAlign: 'middle', overflow: 'hidden', transition: 'background-color 0.2s' }}>
                                                            <Box>
                                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#212121', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {(row.name && row.name.length > (isNarrowScreen ? 12 : 25)) ? `${row.name.substring(0, isNarrowScreen ? 12 : 25)}...` : (row.name || "-")}
                                                                </Typography>
                                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: isNarrowScreen ? '11px' : '14px', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.mobile || "-"}</Typography>
                                                            </Box>
                                                        </TableCell>

                                                        <TableCell sx={{ px: 0, width: isNarrowScreen ? '14%' : '14%', verticalAlign: 'middle', transition: 'background-color 0.2s' }}>
                                                            {selectedCategory === "motor" ? (
                                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.vehicleNumber || "-"}</Typography>
                                                            ) : (
                                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.policyCategoryType || "-"}</Typography>
                                                            )}
                                                        </TableCell>


                                                        <TableCell sx={{ px: 0, width: isNarrowScreen ? '15%' : '14%', verticalAlign: 'middle', transition: 'background-color 0.2s' }}>
                                                            <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.policyType || "-"}</Typography>
                                                        </TableCell>


                                                        <TableCell sx={{ px: 0, width: isNarrowScreen ? '11%' : '14%', verticalAlign: 'middle', transition: 'background-color 0.2s' }}>
                                                            <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.policyCompany}>
                                                                {(row.policyCompany && row.policyCompany.length > (isNarrowScreen ? 10 : 18)) ? `${row.policyCompany.substring(0, isNarrowScreen ? 10 : 18)}...` : (row.policyCompany || "-")}
                                                            </Typography>
                                                        </TableCell>


                                                        <TableCell sx={{ px: 0, width: isNarrowScreen ? '14%' : '12%', verticalAlign: 'middle', transition: 'background-color 0.2s', textAlign: isNarrowScreen ? 'left' : 'left', pr: isNarrowScreen ? 1 : 0 }}>
                                                            <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: '#374151', whiteSpace: 'nowrap' }}>₹ {formatAmount(values.netPremium)}</Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ px: 0, width: isNarrowScreen ? '14%' : '12%', verticalAlign: 'middle', transition: 'background-color 0.2s', textAlign: isNarrowScreen ? 'left' : 'left', pr: isNarrowScreen ? 1 : 0 }}>
                                                            <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: isNarrowScreen ? '12px' : isSmallScreen ? '14px' : '16px', color: values.commission > 0 ? '#166534' : '#6B7280', whiteSpace: 'nowrap' }}>
                                                                ₹ {formatAmount(values.commission)}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ px: 0, width: isNarrowScreen ? '13%' : '12%', verticalAlign: 'middle', transition: 'background-color 0.2s' }}>
                                                            <Box sx={{ display: 'flex', justifyContent: isNarrowScreen ? 'flex-end' : 'flex-start', pr: isNarrowScreen ? 3 : 0 }}>
                                                                {values.balance > 0 ? (
                                                                    <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: isNarrowScreen ? '11px' : '14px', color: '#DC2626' }}>Pending</Typography>
                                                                ) : (
                                                                    <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: isNarrowScreen ? '11px' : '14px', color: '#212121' }}>Paid</Typography>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>

                                                    {/* Expanded Row Content */}
                                                    <TableRow sx={{ display: isExpanded ? 'table-row' : 'none' }}>
                                                        <TableCell sx={{ p: 0, border: 'none', width: 48, minWidth: 48 }} />
                                                        <TableCell colSpan={7} sx={{ p: 0, borderBottom: isExpanded ? '1px solid #E5E7EB' : 'none' }}>
                                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                <Box
                                                                    id={`expanded-row-container-${row.id}`}
                                                                    sx={{ p: 2, pl: 2, backgroundColor: '#FAFBFF' }}
                                                                >
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        backgroundColor: '#FFFFFF',
                                                                        borderRadius: '12px',
                                                                        border: '1px solid #E5E7EB',
                                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                                                        width: '100%',
                                                                        position: 'relative',
                                                                        overflow: 'hidden'
                                                                    }}>
                                                                        {/* Left Anchor Strip */}
                                                                        <Box sx={{ width: '4px', backgroundColor: '#3B82F6' }} />

                                                                        <Box sx={{ p: isNarrowScreen ? 2 : 3.5, display: 'flex', flexDirection: 'row', width: '100%', gap: isNarrowScreen ? 3 : 8 }}>
                                                                            {/* Left Column: Donut Chart */}
                                                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pr: isNarrowScreen ? 2 : 5, borderRight: '1px solid #F3F4F6' }}>
                                                                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Payment Overview</Typography>
                                                                                <FinancialDonut received={values.amountReceived} balance={values.balance} size={isNarrowScreen ? 90 : 110} />
                                                                            </Box>

                                                                            {/* Right Columns: Data Groups */}
                                                                            <Box sx={{ flex: 1 }}>
                                                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: isNarrowScreen ? 3 : 6 }}>
                                                                                    {/* Policy Amounts */}
                                                                                    <Box>
                                                                                        <Typography component="div" sx={{ fontWeight: 700, fontSize: 12, color: '#374151', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                            <Box sx={{ width: 3, height: 14, bgcolor: '#3B82F6', borderRadius: 1 }} />
                                                                                            Policy Amounts
                                                                                        </Typography>
                                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                                <Typography sx={{ fontSize: 10, color: '#6B7280' }}>Total Premium</Typography>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                    <Typography sx={{ fontSize: 11, fontWeight: 600, minWidth: '70px', textAlign: 'right' }}>₹ {formatAmount(values.premium)}</Typography>
                                                                                                    <Box sx={{ width: 22 }} />
                                                                                                </Box>
                                                                                            </Box>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                                <Typography sx={{ fontSize: 10, color: '#6B7280' }}>Discount</Typography>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#374151', minWidth: '70px', textAlign: 'right' }}>₹ {formatAmount(values.discount)}</Typography>
                                                                                                    <Box sx={{ width: 22 }} />
                                                                                                </Box>
                                                                                            </Box>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#374151' }}>Amount Payable</Typography>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                    <Typography sx={{ fontSize: 11, fontWeight: 700, minWidth: '70px', textAlign: 'right' }}>₹ {formatAmount(values.amountPayable)}</Typography>
                                                                                                    <Box sx={{ width: 22 }} />
                                                                                                </Box>
                                                                                            </Box>
                                                                                            <Divider sx={{ my: 0.5 }} />
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                                <Typography sx={{ fontSize: 10, color: '#6B7280' }}>Amount Received</Typography>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#22C55E', minWidth: '70px', textAlign: 'right' }}>₹ {formatAmount(values.amountReceived)}</Typography>
                                                                                                    <Box sx={{ width: 22 }} />
                                                                                                </Box>
                                                                                            </Box>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#374151' }}>Balance</Typography>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: values.balance > 0 ? '#DC2626' : '#166534', minWidth: '70px', textAlign: 'right' }}>₹ {formatAmount(values.balance)}</Typography>
                                                                                                    {values.balance > 0 ? (
                                                                                                        <NotificationsIcon
                                                                                                            onClick={(e) => { e.stopPropagation(); handleRemindClick(row, e); }}
                                                                                                            sx={{
                                                                                                                fontSize: 22,
                                                                                                                color: '#1976D2',
                                                                                                                cursor: 'pointer',
                                                                                                                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                                                                '&:hover': {
                                                                                                                    color: '#1565C0',
                                                                                                                    transform: 'scale(1.2) translateY(-1.1px)',
                                                                                                                    filter: 'drop-shadow(0 6px 8px rgba(25, 118, 210, 0.25))'
                                                                                                                },
                                                                                                                '&:active': {
                                                                                                                    transform: 'scale(0.95)'
                                                                                                                }
                                                                                                            }}

                                                                                                        />
                                                                                                    ) : (
                                                                                                        <Box sx={{ width: 22 }} />
                                                                                                    )}
                                                                                                </Box>
                                                                                            </Box>
                                                                                        </Box>
                                                                                    </Box>

                                                                                    {/* Commission */}
                                                                                    <Box>
                                                                                        <Typography component="div" sx={{ fontWeight: 700, fontSize: 12, color: '#374151', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                            <Box sx={{ width: 3, height: 14, bgcolor: '#10B981', borderRadius: 1 }} />
                                                                                            Commission
                                                                                        </Typography>
                                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                                <Typography sx={{ fontSize: 10, color: '#6B7280' }}>Net Premium</Typography>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                    <Typography sx={{ fontSize: 11, fontWeight: 600, minWidth: '70px', textAlign: 'right' }}>₹ {formatAmount(values.netPremium)}</Typography>
                                                                                                    <Box sx={{ width: 22 }} />
                                                                                                </Box>
                                                                                            </Box>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                                <Typography sx={{ fontSize: 10, color: '#6B7280' }}>Commission Earned</Typography>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#166534', minWidth: '70px', textAlign: 'right' }}>₹ {formatAmount(values.commission)}</Typography>
                                                                                                    <Box sx={{ width: 22 }} />
                                                                                                </Box>
                                                                                            </Box>
                                                                                        </Box>
                                                                                    </Box>
                                                                                </Box>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Filter Popover */}
                        <Popover
                            open={Boolean(filterAnchorEl)}
                            anchorEl={filterAnchorEl}
                            onClose={handleFilterClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{ paper: { sx: { p: 2.5, width: 380, maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto' } } }}
                        >
                            <Stack spacing={2}>
                                {/* Company Filter */}
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FormControlLabel
                                            control={<Checkbox size="small" checked={showCompanyOptions} onChange={(e) => handleCompanyCheckboxChange(e.target.checked)} />}
                                            label="Company"
                                            sx={{ mb: 0 }}
                                        />
                                    </Box>
                                    {showCompanyOptions && (
                                        <Box sx={{ pl: 3, pr: 1, maxHeight: 200, overflowY: 'auto' }}>
                                            {(() => {
                                                const companies = selectedCategory === 'motor' ? COMPANIES.motor : selectedCategory === 'life' ? COMPANIES.life : COMPANIES.non_motor;
                                                return companies.map((company) => (
                                                    <FormControlLabel
                                                        key={company}
                                                        control={<Checkbox size="small" checked={selectedCompanies.includes(company)} onChange={() => handleCompanyToggle(company)} />}
                                                        label={company}
                                                        sx={{ mb: 0.5, display: 'block' }}
                                                    />
                                                ));
                                            })()}
                                        </Box>
                                    )}
                                </Box>
                                <Divider />

                                {/* Policy Type Filter */}
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FormControlLabel
                                            control={<Checkbox size="small" checked={showPolicyTypeOptions} onChange={(e) => handlePolicyTypeCheckboxChange(e.target.checked)} />}
                                            label="Policy Type"
                                            sx={{ mb: 0 }}
                                        />
                                    </Box>
                                    {showPolicyTypeOptions && (
                                        <Box sx={{ pl: 3, pr: 1, maxHeight: 200, overflowY: 'auto' }}>
                                            {(() => {
                                                const policyTypes = selectedCategory === 'motor' ? POLICY_TYPES.motor : selectedCategory === 'life' ? POLICY_TYPES.life : POLICY_TYPES.health;
                                                return policyTypes.map((policyType) => (
                                                    <FormControlLabel
                                                        key={policyType}
                                                        control={<Checkbox size="small" checked={(selectedPolicyTypes[selectedCategory] || []).includes(policyType)} onChange={() => handlePolicyTypeToggle(policyType)} />}
                                                        label={policyType}
                                                        sx={{ mb: 0.5, display: 'block' }}
                                                    />
                                                ));
                                            })()}
                                        </Box>
                                    )}
                                </Box>
                                <Divider />

                                {/* Date Range Filter */}
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Date Range (Created)</Typography>
                                    <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                                        <Button variant={showDateRangeCalendar === 'start' ? 'contained' : 'outlined'} size="small" onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'start' ? null : 'start')} sx={{ flex: 1, textTransform: 'none' }}>
                                            {dateRange.start ? dateRange.start.format('DD MMM') : 'Start Date'}
                                        </Button>
                                        <Button variant={showDateRangeCalendar === 'end' ? 'contained' : 'outlined'} size="small" onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'end' ? null : 'end')} sx={{ flex: 1, textTransform: 'none' }} disabled={!Boolean(dateRange.start)}>
                                            {dateRange.end ? dateRange.end.format('DD MMM') : 'End Date'}
                                        </Button>
                                    </Stack>
                                    {showDateRangeCalendar && (
                                        <Box ref={calendarRef} sx={{ position: 'relative', zIndex: 1300, boxShadow: 3, borderRadius: 2, bgcolor: 'background.paper', width: '100%', overflow: 'hidden', padding: '12px', mb: 1.5, minHeight: '320px', maxHeight: '320px' }}>
                                            <Calendar value={showDateRangeCalendar === 'start' ? dateRange.start?.toDate() : dateRange.end?.toDate()} onChange={handleDateRangeCalendarChange} calendarType="gregory" locale="en" maxDetail="month" minDetail="month" />
                                        </Box>
                                    )}
                                </Box>
                                <Divider />

                                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                    <Button onClick={clearFilters} size="small" color="inherit" sx={{ '&:hover': { backgroundColor: '#F5F5F5' } }}>Clear filters</Button>
                                    <Button onClick={handleFilterClose} size="small" variant="contained">Done</Button>
                                </Stack>
                            </Stack>
                        </Popover>

                        {/* Spacer */}
                        <Box sx={{ flexGrow: 1 }} />

                        {/* Pagination - inside the table container */}
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto', pt: 2, pb: 1 }}>
                                <Pagination
                                    count={Math.max(totalPages, 1)}
                                    page={currentPage}
                                    onChange={(_, value) => setCurrentPage(value)}
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            borderRadius: '50%',
                                            border: '1px solid #D1D5DB',
                                            width: 36,
                                            height: 36,
                                            margin: '0 4px',
                                            color: '#6B7280',
                                            '&:hover': { backgroundColor: '#E0EBFF' },
                                        },
                                        '& .MuiPaginationItem-previousNext': {
                                            borderRadius: 0,
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            width: 'auto',
                                            height: 'auto',
                                        },
                                        '& .Mui-selected': {
                                            backgroundColor: '#004DC2 !important',
                                            color: '#fff !important',
                                            border: 'none',
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            <RemindDialog
                isOpen={remindDialogOpen}
                onClose={handleRemindClose}
                onConfirm={handleRemindConfirm}
                message="Send balance reminder via WhatsApp?"
            />

            {/* Commission Upload Dialog */}
            <Dialog
                open={uploadDialogOpen}
                onClose={handleUploadClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            backdropFilter: 'blur(2px)',
                        }
                    }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" component="div" sx={{ fontFamily: 'Roboto Slab', fontWeight: 500 }}>
                        Upload Commission Data
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleUploadClose}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ textAlign: "center", pt: 1 }}>
                    {!uploadResult && !uploadLoading && (
                        <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="upload-company-label">Select Company</InputLabel>
                                <Select
                                    labelId="upload-company-label"
                                    value={uploadCompany}
                                    label="Select Company"
                                    onChange={(e) => {
                                        setUploadCompany(e.target.value);
                                        setUploadFile(null);
                                        setUploadError(null);
                                    }}
                                >
                                    <MenuItem value="NEW_INDIA">New India Assurance (PDF)</MenuItem>
                                    <MenuItem value="LIC">LIC (PDF)</MenuItem>
                                    <MenuItem value="STAR">Star (PDF)</MenuItem>
                                    {/* <MenuItem value="GODIGIT">GoDigit (Excel)</MenuItem> */}
                                </Select>
                            </FormControl>

                            <Box
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                sx={{
                                    border: isDragging ? "2px dashed #0063F7" : "2px dashed #ccc",
                                    borderRadius: "4px",
                                    p: 4,
                                    backgroundColor: isDragging ? "#F0F7FF" : "#FAFAFA",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: 180,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': { backgroundColor: '#F0F5FF', borderColor: '#0063F7' }
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <PictureAsPdfIcon sx={{ fontSize: 40, color: "#757575", mb: 1 }} />
                                <Typography
                                    sx={{
                                        fontFamily: "Roboto",
                                        fontWeight: 400,
                                        fontSize: "22px",
                                        lineHeight: "30px",
                                        mb: 1,
                                        textAlign: 'center'
                                    }}
                                >
                                    Drag and drop or click to upload. We'll extract the data for you
                                </Typography>
                                <Button
                                    sx={{
                                        color: "#0063F7",
                                        textTransform: "none",
                                        fontSize: "20px",
                                        "&:hover": { textDecoration: "underline", backgroundColor: "transparent" }
                                    }}
                                >
                                    Choose files
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={uploadCompany === 'GODIGIT' ? '.xlsx,.xls' : '.pdf'}
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                            </Box>
                            {uploadFile && (
                                <Typography sx={{ fontSize: "14px", color: "#666", textAlign: "center" }}>
                                    Selected: {uploadFile.name}
                                </Typography>
                            )}
                            {uploadError && (
                                <Alert severity="error" sx={{ mt: 1 }}>{uploadError}</Alert>
                            )}
                        </Box>
                    )}

                    {uploadLoading && (
                        <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                            <Typography variant="h6">Extracting Commission Data...</Typography>
                            <Box sx={{ width: '100%', px: 4 }}>
                                <LinearProgress sx={{ height: 10, borderRadius: 5, backgroundColor: "#E5E7EB", "& .MuiLinearProgress-bar": { backgroundColor: "#0063F7" } }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">This may take a few moments</Typography>
                        </Box>
                    )}

                    {uploadResult && (
                        <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <CheckCircleIcon sx={{ fontSize: 64, color: '#166534', mb: 1 }} />
                            <Typography
                                variant="h5"
                                sx={{
                                    fontFamily: 'Roboto Slab',
                                    fontWeight: 500,
                                    color: '#166534',
                                    mb: 1
                                }}
                            >
                                Hurray! Your commissions have been updated
                            </Typography>

                            <Paper variant="outlined" sx={{ p: 3, width: '100%', backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }}>
                                <Stack spacing={1} sx={{ textAlign: 'left' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography fontWeight={500}>Total Extracted:</Typography>
                                        <Typography fontWeight={600}>{uploadResult.totalExtracted || 0}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography fontWeight={500} color="#166534">Successfully Updated:</Typography>
                                        <Typography fontWeight={600} color="#166534">{uploadResult.successfullyUpdated || 0}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography fontWeight={500} color="#92400E">Skipped (No matched Policy):</Typography>
                                        <Typography fontWeight={600} color="#92400E">{uploadResult.skipped?.length || 0}</Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                {!uploadLoading && (
                    <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'flex-end' }}>
                        {!uploadResult ? (
                            <>
                                <Button
                                    onClick={handleUploadClose}
                                    sx={{
                                        width: "149px",
                                        height: "60px",
                                        borderRadius: "6px",
                                        borderColor: "#B00020",
                                        color: "#B00020",
                                        fontFamily: "Roboto",
                                        fontWeight: 400,
                                        fontSize: "20px",
                                        lineHeight: "24px",
                                        letterSpacing: "0.01em",
                                        textTransform: "none",
                                        "&:hover": {
                                            backgroundColor: "inherit",
                                            color: "#B00020",
                                            boxShadow: 10
                                        }
                                    }}
                                    variant="outlined"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleUploadSubmit}
                                    disabled={!uploadFile}
                                    variant="contained"
                                    sx={{
                                        width: "149px",
                                        height: "60px",
                                        borderRadius: "6px",
                                        backgroundColor: '#0063F7 !important',
                                        fontFamily: "Roboto",
                                        fontWeight: 400,
                                        fontSize: "20px",
                                        lineHeight: "24px",
                                        letterSpacing: "0.01em",
                                        textTransform: "none",
                                        "&:disabled": {
                                            backgroundColor: "#CCCCCC !important",
                                            color: "#666666 !important",
                                        },
                                        '&:hover': {
                                            backgroundColor: '#0063F7 !important',
                                            color: '#FFFFFF !important',
                                            boxShadow: 10
                                        }
                                    }}
                                >
                                    Submit
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    onClick={handleUploadClose}
                                    variant="outlined"
                                    sx={{
                                        width: "149px",
                                        height: "60px",
                                        borderRadius: "6px",
                                        borderColor: "#B00020",
                                        color: "#B00020",
                                        fontFamily: "Roboto",
                                        fontWeight: 400,
                                        fontSize: "20px",
                                        lineHeight: "24px",
                                        letterSpacing: "0.01em",
                                        textTransform: "none",
                                        "&:hover": {
                                            backgroundColor: "inherit",
                                            color: "#B00020",
                                            boxShadow: 10
                                        }
                                    }}
                                >
                                    Close
                                </Button>
                                <Button
                                    onClick={handleUploadClose}
                                    variant="contained"
                                    sx={{
                                        width: "149px",
                                        height: "60px",
                                        borderRadius: "6px",
                                        backgroundColor: '#0063F7 !important',
                                        fontFamily: "Roboto",
                                        fontWeight: 400,
                                        fontSize: "20px",
                                        lineHeight: "24px",
                                        letterSpacing: "0.01em",
                                        textTransform: "none",
                                        '&:hover': {
                                            backgroundColor: '#0063F7 !important',
                                            color: '#FFFFFF !important',
                                            boxShadow: 10
                                        }
                                    }}
                                >
                                    Done
                                </Button>
                            </>
                        )}
                    </DialogActions>
                )}
            </Dialog>
        </Box >
    );
};

export default AdminFinance;
